/* Which warehouse features are switched on? (#8104)
 *
 * Legacy free-text Zone / Aisle / Bin on the product and the Stocks filter are
 * superseded by real bins, and showing both invites someone to fill in the
 * wrong one. Screens that carry the legacy fields ask this and hide them once a
 * warehouse has location levels; a tenant that never enables the module keeps
 * everything exactly as it was.
 *
 * Expiry (Phase 2) is a SEPARATE switch, not a rider on locations: a channel
 * may want dates without bins, or bins without dates. Both answers come from
 * the same channel list, which is the same for the whole session, so it is
 * fetched once and shared rather than re-asked by every screen that needs it.
 */
import { useEffect, useState } from "react";
import { getChannels } from "../../_redux/stocks/stocksCrud";

let pending = null;

/* The cached answer can flip when the user saves a channel's location levels
 * or track-expiry switch on LocationsPage — call this right after such a save
 * so the next asker refetches instead of keeping the stale session-wide
 * answer. */
export function resetChannelsCache() {
  pending = null;
}

export function channelsOnce() {
  if (!pending) {
    pending = getChannels({ page_size: -1 })
      .then((res) => res.data?.entities || res.data?.data?.entities || [])
      .catch((err) => {
        // A user without ChannelView must not lose the legacy fields they rely
        // on, so an empty list reads as "nothing is switched on" — but don't
        // cache the failure either: one flaky request used to hide the module
        // for the rest of the session with no way back.
        pending = null;
        // eslint-disable-next-line no-console
        console.warn("Could not determine which warehouse features are enabled", err);
        return [];
      });
  }
  return pending;
}

export function locationsEnabledOnce() {
  return channelsOnce().then((list) => list.some((channel) => channel?.location_levels?.length));
}

export function expiryEnabledOnce(channelId) {
  return channelsOnce().then((list) => {
    // No channel named: is it on anywhere? That is what a menu entry or a
    // whole-page guard needs to know.
    if (!channelId) return list.some((channel) => channel?.track_expiry);
    return Boolean(list.find((channel) => Number(channel?.id) === Number(channelId))?.track_expiry);
  });
}

function useResolved(resolve, deps) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    resolve().then((value) => {
      if (alive) setEnabled(value);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return enabled;
}

export function useLocationsEnabled() {
  return useResolved(locationsEnabledOnce, []);
}

/** Does ANY channel track expiry? For menu and page-level guards. */
export function useExpiryEnabledAnywhere() {
  return useResolved(() => expiryEnabledOnce(), []);
}

/** Does THIS channel track expiry?
 *
 * Returns false until a real channel id arrives. The previous version forwarded
 * a null id straight through to expiryEnabledOnce, which reads that as "ask
 * anywhere" — so a screen still waiting for its order's channel briefly got the
 * answer for a different warehouse, and rendered expiry dates that then
 * vanished. Ask the explicit question when that is what you mean. */
export function useExpiryEnabled(channelId) {
  const resolved = useResolved(() => (channelId ? expiryEnabledOnce(channelId) : Promise.resolve(false)), [channelId]);
  return channelId ? resolved : false;
}

export default useLocationsEnabled;
